import { Injectable, NgZone } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SafeResourceUrl, SafeUrl } from '@angular/platform-browser';
import { ApolloQueryResult } from 'apollo-client';
import { Logger } from 'aws-amplify';
import { AmplifyService } from 'aws-amplify-angular';
import { saveAs } from 'file-saver';
import gql from 'graphql-tag';
import { Observable, defer, forkJoin, from, of } from 'rxjs';
import { catchError, finalize, map, mergeMap, switchMap } from 'rxjs/operators';
import { ErrorHandlerService } from 'src/app/core/api/error-handler.service';
import { AppSyncService } from 'src/app/core/appsync.service';
import { environment } from 'src/environments/environment';
import { getObjectS3PreSignedUrl, publicUploadS3PreSignedUrl } from 'src/graphql/queries';
import { v4 as uuid } from 'uuid';
import {
  DummyS3ObjectQueryQuery,
  GetObjectS3PreSignedUrlQuery,
  PublicUploadS3PreSignedUrlQuery
} from '../../../API';
import { LoadingSpinnerService } from '../loading-spinner/loading-spinner.service';
import { ContentType, ImageToDataUriPipe } from '../shared-pipes/image-to-data-uri.pipe';
import { S3ObjectInput } from './../../../API';
import { Attachment } from './attachments.component';

export interface S3ObjectConfig {
  bucketName: string;
  region: string;
  key: string;
  metadata: string;
}

export interface PublicS3PreSignedUrlConfig {
  type: PublicS3PreSignedUrlType;
  config: any;
}

export interface GetObjectConfig extends PublicS3PreSignedUrlConfig {
  type: PublicS3PreSignedUrlType.getObject;
  config: S3ObjectInput;
}

export interface PutObjectConfig extends PublicS3PreSignedUrlConfig {
  type: PublicS3PreSignedUrlType.putObject;
  config: {
    filename: string;
    clinicId: string;
    contentType: string;
  };
}

export enum PublicS3PreSignedUrlType {
  getObject = 'getObject',
  putObject = 'putObject'
}

export type S3Object = DummyS3ObjectQueryQuery['dummyS3ObjectQuery'];
export type UrlWithContentType = {
  url: string | SafeResourceUrl | SafeUrl;
  contentType: ContentType;
  fileName?: string;
};

@Injectable({
  providedIn: 'root'
})
export class S3AttachmentService {
  private logger: Logger = new Logger('S3AttachmentService');

  constructor(
    private amplifyService: AmplifyService,
    private loadingSpinnerService: LoadingSpinnerService,
    private appSyncService: AppSyncService,
    private imageToDataUriPipe: ImageToDataUriPipe,
    // NgZone is injected so that fetch-based upload callbacks are brought back
    // into Angular's zone, triggering change detection in subscribers
    private ngZone: NgZone,
    private snackBar: MatSnackBar
  ) {}

  public getPresignedUrl(s3Object: S3Object): Promise<string> {
    const options = this.getS3DownloadOptions(s3Object.key);
    return this.amplifyService.storage().get(options.key, {
      bucket: s3Object.bucket,
      region: s3Object.region,
      ...options
    }) as Promise<string>;
  }

  private getS3Object(s3Object: S3Object, download = true): Observable<any> {
    const isViaPublicS3PreSignedUrl = JSON.parse(s3Object.metadata || '{}').viaPublicS3PreSignedUrl;
    return isViaPublicS3PreSignedUrl
      ? this.getObjectUsingPreSignedUrl(s3Object)
      : this.getObjectUsingBucket(s3Object, download);
  }

  private getObjectUsingBucket(s3Object: S3Object, download: boolean): Observable<any> {
    const options = this.getS3DownloadOptions(s3Object.key);
    return from(
      this.amplifyService.storage().get(options.key, {
        bucket: s3Object.bucket,
        region: s3Object.region,
        download: download,
        ...options
      })
    );
  }

  public download(s3Object: S3Object) {
    if (!s3Object || !s3Object.key) {
      this.showDownloadError(new Error('Cannot download a file with no S3 object'));
      return;
    }

    this.loadingSpinnerService.show();
    // defer() keeps the synchronous throw from getS3DownloadOptions inside the
    // stream, so finalize() always releases the spinner
    defer(() => this.getS3Object(s3Object))
      .pipe(finalize(() => this.loadingSpinnerService.hide()))
      .subscribe(
        value =>
          saveAs(new Blob([value.Body], { type: value.ContentType }), this.getFileName(s3Object)),
        error => this.showDownloadError(error)
      );
  }

  private showDownloadError(error: any) {
    console.error('[S3AttachmentService] Download failed:', error);
    ErrorHandlerService.trySendingSentryError(error);
    this.snackBar.open(
      'This file could not be downloaded. It may no longer be available. Please contact support@csidryeye.com if this continues.',
      'Dismiss',
      { duration: 6000, verticalPosition: 'bottom' }
    );
  }

  public s3ObjectToUri(s3Object: S3Object): Observable<UrlWithContentType> {
    this.logger.debug('getS3ImageDataUri');

    return this.getS3Object(s3Object).pipe(
      catchError(e => {
        console.log(e);
        return of(null);
      }),
      switchMap(imageData =>
        imageData
          ? this.imageToDataUriPipe.transform(imageData.Body, imageData.ContentType, true).pipe(
              map(url => {
                return { url: url, contentType: imageData.ContentType };
              })
            )
          : of(null)
      )
    );
  }

  private getPresignedUrlForGetObject(s3Object: S3Object): Observable<any> {
    const { __typename, ...s3ObjectInput } = s3Object as any;
    return this.getGetObjectS3PreSignedUrl(s3ObjectInput).pipe(
      catchError(err => {
        console.error(`Failed to get URL for ${s3Object.fileName || 'file'}:`, err);
        return of(null);
      })
    );
  }

  private getObjectUsingPreSignedUrl(
    s3Object: S3Object
  ): Observable<{ Body: Blob; ContentType: string }> {
    return this.getPresignedUrlForGetObject(s3Object).pipe(
      mergeMap(publicS3PreSignedUrlResponse => {
        if (!publicS3PreSignedUrlResponse) {
          throw new Error(`Failed to get a download URL for ${s3Object.fileName || 'file'}`);
        }
        if (publicS3PreSignedUrlResponse.statusCode !== 200) {
          throw new Error(`HTTP ${publicS3PreSignedUrlResponse.statusCode}`);
        }
        return from(fetch(publicS3PreSignedUrlResponse.url, { method: 'GET' }));
      }),
      mergeMap(s3ObjectResponse => from(s3ObjectResponse.blob())),
      map(blob => ({
        Body: blob,
        ContentType: blob.type
      }))
    );
  }

  private getS3DownloadOptions(key: string) {
    const match: RegExpMatchArray = /([^\/]+)\/([^\/]+)\/(.*)/.exec(key);
    if (match && match.length === 4) {
      return {
        level: match[1],
        identityId: match[2],
        key: match[3],
        expires: 5 * 60
      };
    } else {
      throw new Error('S3Object key does not match expected pattern');
    }
  }

  public getFileName(s3Object: S3Object): string {
    if (s3Object && s3Object.fileName) {
      return s3Object.fileName;
    } else {
      const match: RegExpMatchArray = /.*\/(.*)/.exec(s3Object.key);
      return match && match.length === 2 ? match[1] : null;
    }
  }

  public uploadToSpecificS3Bucket(s3Config: S3ObjectConfig, attachment: Attachment) {
    if (attachment instanceof File) {
      const file: File = attachment as File;
      const extension: string = /(?:\.([^.]+))?$/.exec(file.name)[0];
      const s3ObjectInput = {
        bucket: s3Config.bucketName,
        region: s3Config.region,
        // @ts-ignore - appsync client accepts localUri as type of "File" but GraphQL does not have "File" scalar
        localUri: file as string,
        mimeType: file.type,
        fileName: file.name,
        metadata: s3Config.metadata,
        key: s3Config.key
      };

      if (s3Config['metadata']) {
        s3ObjectInput['metadata'] = s3Config['metadata'];
      }

      return s3ObjectInput;
    } else {
      const s3object: S3Object = attachment as S3Object;
      delete s3object.__typename;
      return s3object as S3ObjectInput;
    }
  }

  public convertToS3ObjectInput(identityId: string, attachment: Attachment) {
    if (attachment instanceof File) {
      const file: File = attachment as File;
      const extension: string = /(?:\.([^.]+))?$/.exec(file.name)[0];

      const s3ObjectInput = {
        bucket: environment.userUploads.bucket,
        region: environment.userUploads.region,
        // @ts-ignore - appsync client accepts localUri as type of "File"
        localUri: file as string,
        mimeType: file.type,
        fileName: file.name,
        key: 'protected/' + identityId + '/' + uuid() + extension
      };

      if (attachment['metadata']) {
        s3ObjectInput['metadata'] = attachment['metadata'];
      }

      return s3ObjectInput;
    } else {
      const s3object: S3Object = attachment as S3Object;
      delete (s3object as any).__typename;
      return s3object as S3ObjectInput;
    }
  }

  /**
   * Upload a recording file to dry-eye-app-uploads via Amplify Storage (protected level)
   * and return the resulting S3 object. This is used by the AI Scribe so the Lambda
   * reads from the same bucket as all other assessment attachments.
   */
  uploadRecording(file: File, identityId: string): Observable<any> {
    const filename = file.name; // use the name built by the caller (includes doctor/patient/date)

    return from(
      this.amplifyService.storage().put(filename, file, {
        level: 'protected',
        contentType: file.type,
        bucket: environment.userUploads.bucket,
        region: environment.userUploads.region
      }) as Promise<{ key: string }>
    ).pipe(
      map(() => ({
        key: 'protected/' + identityId + '/' + filename,
        bucket: environment.userUploads.bucket,
        region: environment.userUploads.region,
        fileName: filename,
        metadata: null
      }))
    );
  }

  /**
   * Upload files to S3 via presigned PUT URLs and return the resulting S3 objects.
   *
   * The internal fetch() calls run outside Angular's zone. We wrap the final
   * emission in ngZone.run() so that any subscriber that updates component state
   * automatically triggers change detection — even if the subscriber doesn't
   * explicitly call detectChanges().
   */
  uploadFiles(files: File[], clinicId: string): Observable<Attachment[]> {
    return this.getPresignedUrlsForPutObject(files, clinicId).pipe(
      switchMap(results => {
        const uploads = files
          .map((file, index) => ({ file, data: results[index] }))
          .filter(item => item.data && item.data['url']);

        if (uploads.length === 0) {
          // No valid presigned URLs — surface as an error so callers can handle it
          throw new Error('Failed to obtain presigned upload URLs');
        }

        return forkJoin(
          uploads.map(item =>
            this.uploadToS3(item.file, item.data.url).pipe(
              map(() => item.data.bucketInfo as Attachment),
              catchError(err => {
                console.error(`[S3AttachmentService] Upload failed for ${item.file.name}:`, err);
                // Re-throw so the subscriber's error handler is called rather than
                // silently returning a bucketInfo that may have a failed upload
                throw err;
              })
            )
          )
        );
      }),
      // Bring the emission back into Angular's zone so change detection fires
      // in any component that subscribes, without requiring ngZone.run() at
      // every call site
      map(attachments => {
        return this.ngZone.run(() => attachments);
      })
    );
  }

  private uploadToS3(file: File, url: string): Observable<void> {
    return from(
      fetch(url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type }
      })
    ).pipe(
      switchMap(response => {
        if (!response.ok) {
          throw new Error(`S3 PUT failed with HTTP ${response.status}`);
        }
        return of(undefined as void);
      })
    );
  }

  getPresignedUrlsForPutObject(files: File[], clinicId: string): Observable<any[]> {
    const urlRequests = files.map(file => {
      const config = {
        clinicId,
        filename: file.name,
        contentType: file.type
      };
      return this.getPublicUploadS3PreSignedUrl(config).pipe(
        catchError(err => {
          console.error(`[S3AttachmentService] Failed to get presigned URL for ${file.name}:`, err);
          return of(null);
        })
      );
    });

    return forkJoin(urlRequests).pipe(map(urls => urls.filter(url => url !== null) as any[]));
  }

  public getPublicUploadS3PreSignedUrl(config: any): Observable<any> {
    return this.appSyncService.hydrated(true).pipe(
      switchMap(client =>
        client.query({
          query: gql(publicUploadS3PreSignedUrl),
          variables: config,
          fetchPolicy: 'network-only'
        })
      ),
      map((result: ApolloQueryResult<PublicUploadS3PreSignedUrlQuery>) => {
        return JSON.parse(result.data.publicUploadS3PreSignedUrl);
      })
    );
  }

  public getGetObjectS3PreSignedUrl(config: any): Observable<any> {
    return this.appSyncService.hydrated().pipe(
      switchMap(client =>
        client.query({
          query: gql(getObjectS3PreSignedUrl),
          variables: { config },
          fetchPolicy: 'network-only'
        })
      ),
      map((result: ApolloQueryResult<GetObjectS3PreSignedUrlQuery>) => {
        return result.data.getObjectS3PreSignedUrl;
      })
    );
  }
}
